import type { LocalDatabase } from '../db/types';
import type { SyncDelta } from '@couplespace/sync';

import { isOnline } from '../connectivity';
import { applyBootstrap, applyDeltas, getLastSeq } from '../db/index';
import { listOutbox, outboxToClientChanges, removeOutboxIds } from '../db/outbox';
import { fetchBootstrap, fetchCatchup, getSyncWebSocketUrl, pushChanges } from './api';
import { abandonWebSocket } from './ws-utils';

type SyncListener = () => void;

export type SyncStatus = 'idle' | 'bootstrapping' | 'syncing' | 'ready' | 'error';

/**
 * Backend reachability, tracked separately from {@link SyncStatus} (which is
 * about whether the local database is usable). `connecting` is the initial /
 * transient state and is intentionally not surfaced as "offline" so the UI does
 * not flash a warning on every load; `offline` is only set after a real failed
 * round-trip to the server.
 */
export type ConnectionStatus = 'online' | 'offline' | 'connecting';

export type SyncConnection = {
  status: ConnectionStatus;
  /** Epoch ms of the last successful sync round-trip, or null if never. */
  lastSyncedAt: number | null;
  /** Local writes still waiting in the outbox to be pushed. */
  pendingCount: number;
  /** Server deltas that failed to apply locally and are held for retry. */
  quarantinedCount: number;
};

const CATCHUP_POLL_MS = 5_000;

export class SyncEngine {
  private database: LocalDatabase | null = null;
  private ws: WebSocket | null = null;
  private wsGeneration = 0;
  private status: SyncStatus = 'idle';
  private error: string | null = null;
  private listeners = new Set<SyncListener>();
  private remoteListeners = new Set<SyncListener>();
  private pushTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private startPromise: Promise<void> | null = null;
  private localDatabasePromise: Promise<LocalDatabase> | null = null;
  private connectivityBound = false;
  private connectionSnapshot: SyncConnection = {
    status: 'connecting',
    lastSyncedAt: null,
    pendingCount: 0,
    quarantinedCount: 0,
  };

  getStatus(): SyncStatus {
    return this.status;
  }

  getError(): string | null {
    return this.error;
  }

  /** Stable snapshot (same reference until a field changes) so subscribers can memoize. */
  getConnection(): SyncConnection {
    return this.connectionSnapshot;
  }

  private updateConnection(patch: Partial<SyncConnection>): void {
    const prev = this.connectionSnapshot;
    const next: SyncConnection = { ...prev, ...patch };
    if (
      next.status === prev.status &&
      next.lastSyncedAt === prev.lastSyncedAt &&
      next.pendingCount === prev.pendingCount &&
      next.quarantinedCount === prev.quarantinedCount
    ) {
      return;
    }
    this.connectionSnapshot = next;
    this.notify();
  }

  private markOnline(): void {
    this.updateConnection({ status: 'online', lastSyncedAt: Date.now() });
  }

  private markOffline(): void {
    this.updateConnection({ status: 'offline' });
  }

  private async refreshPendingCount(): Promise<void> {
    if (!this.database) {
      return;
    }
    try {
      const entries = await listOutbox(this.database);
      this.updateConnection({ pendingCount: entries.length });
    } catch {
      // Non-fatal: the pending badge is best-effort.
    }
  }

  private async refreshQuarantineCount(): Promise<void> {
    if (!this.database) {
      return;
    }
    try {
      const { countQuarantine } = await import('../db/index.js');
      this.updateConnection({ quarantinedCount: await countQuarantine(this.database) });
    } catch {
      // Non-fatal: the failed-sync badge is best-effort.
    }
  }

  subscribe(listener: SyncListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Fires when remote server changes are applied (not local outbox writes). */
  subscribeRemote(listener: SyncListener): () => void {
    this.remoteListeners.add(listener);
    return () => this.remoteListeners.delete(listener);
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private notifyRemote(): void {
    for (const listener of this.remoteListeners) {
      listener();
    }
    this.notify();
  }

  private setStatus(status: SyncStatus, error: string | null = null): void {
    this.status = status;
    this.error = error;
    this.notify();
  }

  async start(): Promise<void> {
    if (this.status === 'ready') {
      return;
    }
    if (!this.startPromise) {
      this.startPromise = this.runStart();
    }
    return this.startPromise;
  }

  private async runStart(): Promise<void> {
    if (this.status === 'ready') {
      return;
    }

    this.setStatus('bootstrapping');
    try {
      this.database = await this.openLocalDatabase();
      const lastSeq = await getLastSeq(this.database);

      if (lastSeq === 0) {
        try {
          const snapshot = await fetchBootstrap();
          await applyBootstrap(this.database, snapshot);
        } catch (err) {
          this.startPromise = null;
          const message = isOnline()
            ? err instanceof Error
              ? err.message
              : 'Sync failed'
            : 'No local data yet. Open the app once while online to sync, then you can work offline.';
          this.setStatus('error', message);
          throw err;
        }
      } else {
        // The sidebar reads the local nav tree as soon as the database is open.
        // Yield so that read is queued on the SQLite chain before catchup, which
        // otherwise holds the only connection until every delta is applied.
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 0);
        });
        // Push local outbox before pulling server deltas so offline edits are not overwritten.
        await this.flushOutbox();
        await this.catchup(false);
      }

      if (isOnline()) {
        await this.flushOutbox();
      }
      this.bindConnectivity();
      this.connectWebSocket();
      this.startPolling();
      this.setStatus('ready');
    } catch (err) {
      this.startPromise = null;
      if (this.status !== 'error') {
        this.setStatus('error', err instanceof Error ? err.message : 'Sync failed');
      }
      throw err;
    }
  }

  async catchup(notify = true): Promise<void> {
    if (!this.database) {
      const { openDatabase } = await import('../db/index.js');
      this.database = await openDatabase();
    }
    this.setStatus('syncing');
    let appliedAny = false;
    try {
      let since = await getLastSeq(this.database);
      let hasMore = true;
      while (hasMore) {
        const response = await fetchCatchup(since);
        if (response.changes.length > 0) {
          await applyDeltas(this.database, response.changes);
          appliedAny = true;
        }
        since = response.seq;
        hasMore = response.hasMore;
      }
      this.markOnline();
      if (appliedAny) {
        await this.refreshQuarantineCount();
      }
    } catch (err) {
      // Offline or network error — keep serving from local SQLite. Log rather
      // than swallow silently so a genuine apply failure is diagnosable instead
      // of looking like an idle client, and flip the connection banner to
      // "offline" so the user knows they are seeing local-only data.
      console.error('[CS-SYNC] catchup failed', err);
      this.markOffline();
    }
    if (this.database) {
      this.setStatus('ready');
    }
    if (notify && appliedAny) {
      this.notifyRemote();
    }
  }

  schedulePush(): void {
    if (this.pushTimer) {
      clearTimeout(this.pushTimer);
    }
    this.pushTimer = setTimeout(() => {
      void this.flushOutbox();
    }, 300);
  }

  async flushOutbox(): Promise<void> {
    if (!this.database) {
      return;
    }
    const entries = await listOutbox(this.database);
    this.updateConnection({ pendingCount: entries.length });
    if (entries.length === 0) {
      return;
    }

    try {
      const response = await pushChanges(outboxToClientChanges(entries));
      const appliedIds = entries
        .filter((_, index) => {
          const result = response.results[index];
          return result?.status === 'applied' || result?.status === 'conflict';
        })
        .map((entry) => entry.id);
      await removeOutboxIds(this.database, appliedIds);

      const { updateRowSyncVersion } = await import('../db/queries');
      for (let i = 0; i < entries.length; i++) {
        const result = response.results[i];
        const entry = entries[i];
        if (
          result &&
          (result.status === 'applied' || result.status === 'conflict') &&
          result.syncVersion !== undefined &&
          entry.row &&
          typeof entry.row === 'object' &&
          'id' in entry.row
        ) {
          await updateRowSyncVersion(
            this.database,
            entry.tableName,
            String((entry.row as { id: string }).id),
            result.syncVersion,
          );
        }
      }

      await this.refreshPendingCount();
      this.markOnline();

      if (response.results.some((r) => r.status === 'conflict')) {
        await this.catchup(true);
      } else {
        this.notify();
      }
    } catch {
      // Remain in outbox for retry when back online; surface the disconnect.
      this.markOffline();
    }
  }

  private bindConnectivity(): void {
    if (this.connectivityBound || typeof window === 'undefined') {
      return;
    }
    this.connectivityBound = true;
    window.addEventListener('online', () => {
      if (!this.database) {
        return;
      }
      void this.flushOutbox().then(() => this.catchup(true));
    });
    window.addEventListener('offline', () => {
      this.markOffline();
    });
  }

  private startPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
    this.pollTimer = setInterval(() => {
      if (this.status !== 'ready') {
        return;
      }
      // Keep trying every tick so a dropped connection self-heals: a successful
      // catchup/flush flips the banner back to online; a failure flips it to
      // offline. When the browser itself reports no network, skip the doomed
      // fetch but still reflect the offline state.
      if (!isOnline()) {
        this.markOffline();
        return;
      }
      void this.flushOutbox().then(() => this.catchup(true));
    }, CATCHUP_POLL_MS);
  }

  private connectWebSocket(): void {
    if (typeof WebSocket === 'undefined') {
      return;
    }

    const generation = ++this.wsGeneration;
    abandonWebSocket(this.ws);
    this.ws = null;

    const sincePromise = this.database ? getLastSeq(this.database) : Promise.resolve(0);

    void sincePromise.then(async (cursor) => {
      if (generation !== this.wsGeneration) {
        return;
      }
      const ws = new WebSocket(await getSyncWebSocketUrl());
      if (generation !== this.wsGeneration) {
        abandonWebSocket(ws);
        return;
      }
      this.ws = ws;

      ws.onopen = () => {
        if (generation !== this.wsGeneration) {
          return;
        }
        this.markOnline();
        ws.send(JSON.stringify({ type: 'subscribe', since: cursor }));
      };

      ws.onmessage = (event) => {
        void this.handleWsMessage(String(event.data));
      };

      ws.onclose = () => {
        if (generation !== this.wsGeneration) {
          return;
        }
        this.ws = null;
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
        }
        this.reconnectTimer = setTimeout(() => {
          if (this.status === 'ready') {
            this.connectWebSocket();
          }
        }, 3000);
      };
    });
  }

  private async handleWsMessage(raw: string): Promise<void> {
    try {
      const message = JSON.parse(raw) as { type: string; seq?: number; changes?: SyncDelta[] };
      if (message.type === 'ping') {
        this.ws?.send(JSON.stringify({ type: 'pong' }));
        return;
      }
      if (message.type === 'delta' && message.changes && message.changes.length > 0 && this.database) {
        await applyDeltas(this.database, message.changes);
        await this.refreshQuarantineCount();
        this.notifyRemote();
      }
      if (message.type === 'ack') {
        await this.flushOutbox();
      }
    } catch {
      // ignore malformed frames
    }
  }

  async stop(): Promise<void> {
    if (this.pushTimer) {
      clearTimeout(this.pushTimer);
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
    this.ws?.close();
    this.ws = null;
    this.startPromise = null;
    this.setStatus('idle');
  }

  getDatabase(): LocalDatabase | null {
    return this.database;
  }

  /** Open the local database without waiting for network catchup. */
  async openLocalDatabase(): Promise<LocalDatabase> {
    if (this.database) {
      return this.database;
    }
    if (!this.localDatabasePromise) {
      this.localDatabasePromise = (async () => {
        try {
          const { openDatabase } = await import('../db/index.js');
          const db = await openDatabase();
          this.database = db;
          return db;
        } catch (err) {
          this.localDatabasePromise = null;
          throw err;
        }
      })();
    }
    return this.localDatabasePromise;
  }

  async resyncAfterWorkspaceWipe(): Promise<void> {
    if (!this.database) {
      const { openDatabase } = await import('../db/index.js');
      this.database = await openDatabase();
    }
    const { clearSyncedTables } = await import('@couplespace/sync');
    const { applyBootstrap, createExecutor, setLastSeq } = await import('../db/shared.js');
    const { listOutbox, removeOutboxIds } = await import('../db/outbox.js');
    const { fetchBootstrap } = await import('./api.js');
    const exec = createExecutor(this.database);
    await clearSyncedTables(exec);
    await setLastSeq(this.database, 0);
    const outbox = await listOutbox(this.database);
    if (outbox.length > 0) {
      await removeOutboxIds(
        this.database,
        outbox.map((e) => e.id),
      );
    }
    await applyBootstrap(this.database, await fetchBootstrap());
    this.setStatus('ready');
    this.notify();
  }

  /**
   * User-initiated recovery: push any pending local edits, then discard the
   * local synced tables and re-download the server's current state. Unlike
   * {@link resyncAfterWorkspaceWipe}, local edits are flushed first (not
   * dropped) so a resync to fix divergence never loses unsynced work.
   */
  async fullResync(): Promise<void> {
    if (!this.database) {
      const { openDatabase } = await import('../db/index.js');
      this.database = await openDatabase();
    }
    this.setStatus('syncing');
    try {
      await this.flushOutbox();
      const { clearSyncedTables } = await import('@couplespace/sync');
      const { applyBootstrap, createExecutor, setLastSeq } = await import('../db/shared.js');
      const { clearAllQuarantine } = await import('../db/index.js');
      const { fetchBootstrap } = await import('./api.js');
      const exec = createExecutor(this.database);
      await clearSyncedTables(exec);
      await clearAllQuarantine(this.database);
      await setLastSeq(this.database, 0);
      await applyBootstrap(this.database, await fetchBootstrap());
      await this.refreshQuarantineCount();
      this.markOnline();
      this.setStatus('ready');
      this.notifyRemote();
      this.connectWebSocket();
    } catch (err) {
      this.markOffline();
      this.setStatus('ready');
      throw err;
    }
  }

  /** Re-attempt every quarantined delta now; returns the count still failing. */
  async retryFailedItems(): Promise<number> {
    if (!this.database) {
      return 0;
    }
    const { drainQuarantine } = await import('../db/index.js');
    await drainQuarantine(this.database);
    await this.refreshQuarantineCount();
    this.notifyRemote();
    return this.connectionSnapshot.quarantinedCount;
  }

  async getQuarantineEntries(): Promise<import('../db/quarantine').QuarantineEntry[]> {
    if (!this.database) {
      return [];
    }
    const { listQuarantine } = await import('../db/index.js');
    return listQuarantine(this.database);
  }

  /** Reset singleton state between Node test runs. */
  __testOnlyReset(): void {
    this.database = null;
    this.status = 'idle';
    this.error = null;
    this.startPromise = null;
    this.connectionSnapshot = {
      status: 'connecting',
      lastSyncedAt: null,
      pendingCount: 0,
      quarantinedCount: 0,
    };
  }

  /** Inject in-memory SQLite and mark ready (unit tests only). */
  __testOnlyInjectDatabase(db: LocalDatabase): void {
    this.database = db;
    this.status = 'ready';
    this.startPromise = Promise.resolve();
  }

  __testOnlySetStatus(status: SyncStatus): void {
    this.status = status;
  }
}

let engineInstance: SyncEngine | null = null;

export async function flushPendingSync(): Promise<void> {
  const engine = getSyncEngine();
  if (!engine.getDatabase()) {
    return;
  }
  await engine.flushOutbox();
}

export function getSyncEngine(): SyncEngine {
  if (!engineInstance) {
    engineInstance = new SyncEngine();
  }
  return engineInstance;
}

/** @internal */
export function __testOnlyResetSyncEngine(): void {
  engineInstance?.__testOnlyReset();
  engineInstance = null;
}
